import { call, put, select } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ACLSimpleUserPrivilege } from 'models/ACL';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectIsUserInternal } from 'saga/auth/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { selectContextCustomerAccountInfo } from 'saga/context/customerAccount/ducks';
import { selectContextInternalIsUserScreenFilterDisabled } from 'saga/context/internal/ducks';
import { selectContextTariffSpaceInfo } from 'saga/context/tariffSpace/ducks';
import {
  ACLActionTypes,
  getACLKey,
  initialState,
  loadSimpleUserPrivilegeListActions,
  selectACLContextCpRefNo,
  selectACLRequestCpRefNo,
  selectACLState,
  selectUserIdentifiersForUserAccessStoraging,
  setLoadingAction,
} from 'saga/acl/ducks';

export const simpleUserLoadingStatus = createLoadingStateReducer(
  initialState.simpleUserLoadingStatus,
  {
    [ACLActionTypes.LoadSimpleUserPrivilegeList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [ACLActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

export const simpleUserPrivileges = createReducer(initialState.simpleUserPrivileges, {
  [ACLActionTypes.LoadSimpleUserPrivilegeList]: {
    [RequestActionTypes.SUCCESS]: (
      state: Record<string, ACLSimpleUserPrivilege>,
      payload: { data: string[]; key: string }
    ) => ({
      ...state,
      [payload.key]: payload.data.reduce(
        (acc: ACLSimpleUserPrivilege, item: string) => ({
          ...acc,
          [item]: {
            isGranted: true,
            recordTime: new Date(),
          },
        }),
        {}
      ),
    }),
  },
});

/* SELECTORS */
const selectACLSimpleUserPrivileges = (state: AppState): Record<string, ACLSimpleUserPrivilege> =>
  selectACLState(state).simpleUserPrivileges;

const selectGetSimpleUserPrivileges = (
  state: AppState
): ((entityRefNo: string) => ACLSimpleUserPrivilege | undefined) => {
  const keyPart = selectUserIdentifiersForUserAccessStoraging(state);

  return (entityRefNo: string) => selectACLSimpleUserPrivileges(state)[`${keyPart}|${entityRefNo}`];
};

export const selectExistsSimpleUserPrivileges = (state: AppState): ((entityRefNo?: string) => boolean) => {
  return (entityRefNo?: string) =>
    !!selectGetSimpleUserPrivileges(state)(selectContextAclEntityRefNo(state, entityRefNo));
};

export const selectHasSimpleUserPrivilege = (
  state: AppState
): ((privilegeType: string, entityRefNo?: string) => boolean) => {
  return (privilegeType: string, entityRefNo?: string) => {
    const privileges = selectGetSimpleUserPrivileges(state)(selectContextAclEntityRefNo(state, entityRefNo));

    if (privileges) {
      return privileges[privilegeType]?.isGranted;
    }

    return false;
  };
};

const selectContextAclEntityRefNo = (state: AppState, preferredEntityRefNo?: string): string => {
  const screenFilterDisabled = selectContextInternalIsUserScreenFilterDisabled(state);

  if (screenFilterDisabled) {
    return '';
  }

  return (
    preferredEntityRefNo ||
    selectContextTariffSpaceInfo(state)?.tsRefNo ||
    selectContextCustomerAccountInfo(state)?.caRefNo ||
    selectContextCustomerInfo(state)?.cuRefNo ||
    ''
  );
};

export const selectSimpleUserPrivilegeLoading = (state: AppState): LoadingStatus =>
  selectACLState(state).simpleUserLoadingStatus;

export const selectAvailablePrivileges = (
  state: AppState,
  entityRefNo: string,
  privilegeTypes: string[]
): Record<string, boolean> => {
  const hasSimpleUserPrivilege: (privilegeType: string, entityRefNo?: string) => boolean =
    selectHasSimpleUserPrivilege(state);
  const screenFilterDisabled: boolean = selectContextInternalIsUserScreenFilterDisabled(state);
  const finalEntityRefNo = screenFilterDisabled ? '' : entityRefNo;

  const availablePrivileges: Record<string, boolean> = privilegeTypes.reduce(
    (acc, privilegeType) => ({
      ...acc,
      [privilegeType]: hasSimpleUserPrivilege(privilegeType, finalEntityRefNo),
    }),
    {}
  );

  return availablePrivileges;
};

/* SAGAS */
export function* loadSimpleUserPrivilegeList({ payload }: any) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const isScreenFilterDisabled: boolean = yield select(selectContextInternalIsUserScreenFilterDisabled);
  const isInternal: boolean = yield select(selectIsUserInternal);
  let cpRefNo: string | undefined = yield select(selectACLRequestCpRefNo);
  const contextAclEntityRefNo: string = yield select(selectContextAclEntityRefNo, payload.entityRefNo);
  let entityRefNo: string | undefined = contextAclEntityRefNo
    ? contextAclEntityRefNo
    : yield select(selectACLContextCpRefNo);

  if (isScreenFilterDisabled) {
    entityRefNo = undefined;
  }

  if (isScreenFilterDisabled || !isInternal) {
    cpRefNo = undefined;
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.getAclSimpleUserPrivilegeList,
      yield getApiUrl(environment.urlConfig.userAccess),
      cpRefNo,
      entityRefNo,
      payload.forceFreshData
    ),
    skipUserActivityLog: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const keyPart: string = yield select(selectUserIdentifiersForUserAccessStoraging);

    yield put(
      loadSimpleUserPrivilegeListActions.success({
        data: resp.data,
        key: getACLKey(keyPart, entityRefNo),
      })
    );
  } else {
    yield put(loadSimpleUserPrivilegeListActions.failure());
  }
}

export function* getAvailablePrivileges(entityRefNo: string, privilegeTypes: string[]) {
  const existsSimpleUserPrivileges: (entityRefNo: string) => boolean = yield select(selectExistsSimpleUserPrivileges);

  const privilegesExists = existsSimpleUserPrivileges(entityRefNo);

  if (!privilegesExists) {
    const payload = {
      entityRefNo,
      forceFreshData: true,
    };

    yield call(loadSimpleUserPrivilegeList, { payload });
  }

  const availablePrivileges: Record<string, boolean> = yield select(
    selectAvailablePrivileges,
    entityRefNo,
    privilegeTypes
  );

  return availablePrivileges;
}
